// SMS Notification Service
// Supports: MSG91, Twilio, Fast2SMS

const SMS_PROVIDER = process.env.REACT_APP_SMS_PROVIDER || 'msg91';

class SMSService {
  constructor() {
    this.provider = SMS_PROVIDER;

    // MSG91 credentials
    this.msg91AuthKey = process.env.REACT_APP_MSG91_AUTH_KEY;
    this.msg91SenderId = process.env.REACT_APP_MSG91_SENDER_ID || 'ODISHA';
    this.msg91Route = process.env.REACT_APP_MSG91_ROUTE || '4'; // 4 = Transactional

    // Twilio credentials
    this.twilioAccountSid = process.env.REACT_APP_TWILIO_ACCOUNT_SID;
    this.twilioAuthToken = process.env.REACT_APP_TWILIO_AUTH_TOKEN;
    this.twilioPhoneNumber = process.env.REACT_APP_TWILIO_PHONE_NUMBER;

    // Fast2SMS credentials
    this.fast2smsApiKey = process.env.REACT_APP_FAST2SMS_API_KEY;

    console.log(`📨 SMS Service initialized with provider: ${this.provider}`);
  }

  /**
   * Send order confirmation SMS
   */
  async sendOrderConfirmation(orderData) {
    const { customer, orderNumber, total, paymentMethod } = orderData;

    const message = `Odisha Zaika: Order #${orderNumber} confirmed! ` +
      `Amount: Rs.${total}. Payment: ${paymentMethod === 'online' ? 'Paid' : 'COD'}. ` +
      `Track: ${window.location.origin}/track-order`;

    return await this.sendSMS(customer.phone, message, 'order_confirmation');
  }

  /**
   * Send order status update SMS
   */
  async sendOrderStatusUpdate(orderData, newStatus) {
    const { customer, orderNumber } = orderData;

    const statusMessages = {
      'in_progress': `Odisha Zaika: Order #${orderNumber} is being prepared!`,
      'prepared': `Odisha Zaika: Order #${orderNumber} is ready for delivery!`,
      'out_for_delivery': `Odisha Zaika: Order #${orderNumber} is on the way!`,
      'delivered': `Odisha Zaika: Order #${orderNumber} delivered! Enjoy your meal!`,
      'cancelled': `Odisha Zaika: Order #${orderNumber} cancelled. Contact: 7873611363`
    };

    const message = statusMessages[newStatus] || `Order #${orderNumber} status: ${newStatus}`;

    return await this.sendSMS(customer.phone, message, 'status_update');
  }

  /**
   * Send delivery person assigned SMS
   */
  async sendDeliveryAssigned(orderData, deliveryBoyData) {
    const { customer, orderNumber } = orderData;
    const { name, phone } = deliveryBoyData;

    const message = `Odisha Zaika: ${name} is delivering order #${orderNumber}. ` +
      `Contact: ${phone}. Track live location in app.`;

    return await this.sendSMS(customer.phone, message, 'delivery_assigned');
  }

  /**
   * Send OTP for verification
   */
  async sendOTP(phoneNumber, otp) {
    const message = `Your Odisha Zaika OTP is: ${otp}. Valid for 10 minutes. Do not share.`;

    return await this.sendSMS(phoneNumber, message, 'otp');
  }

  /**
   * Send SMS to phone number
   * @param {string} phoneNumber - Recipient phone
   * @param {string} message - SMS text
   * @param {string} messageType - Type for tracking
   */
  async sendSMS(phoneNumber, message, messageType = 'general') {
    try {
      const formattedPhone = this._formatPhoneNumber(phoneNumber);

      console.log(`📤 Sending SMS to ${formattedPhone} (${messageType})`);

      // Check message length (160 chars for single SMS)
      if (message.length > 160) {
        console.warn(`⚠️ SMS length ${message.length} chars (will be split)`);
      }

      if (this.provider === 'msg91') {
        return await this._sendViaMSG91(formattedPhone, message);
      } else if (this.provider === 'twilio') {
        return await this._sendViaTwilio(formattedPhone, message);
      } else if (this.provider === 'fast2sms') {
        return await this._sendViaFast2SMS(formattedPhone, message);
      } else {
        throw new Error('SMS provider not configured');
      }
    } catch (error) {
      console.error('❌ SMS send error:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Send via MSG91 API
   */
  async _sendViaMSG91(phoneNumber, message) {
    if (!this.msg91AuthKey) {
      throw new Error('MSG91 auth key not configured');
    }

    // MSG91 API endpoint (v5)
    const url = 'https://api.msg91.com/api/v5/flow/';

    const payload = {
      sender: this.msg91SenderId,
      route: this.msg91Route,
      country: '91',
      sms: [
        {
          message: message,
          to: [phoneNumber.replace('+91', '')]
        }
      ]
    };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'authkey': this.msg91AuthKey,
        'content-type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json();

    if (response.ok && data.type === 'success') {
      console.log('✅ SMS sent via MSG91:', data.message);
      return {
        success: true,
        provider: 'msg91',
        messageId: data.request_id,
        type: data.type
      };
    } else {
      throw new Error(data.message || 'MSG91 API error');
    }
  }

  /**
   * Send via Twilio SMS API
   */
  async _sendViaTwilio(phoneNumber, message) {
    if (!this.twilioAccountSid || !this.twilioAuthToken || !this.twilioPhoneNumber) {
      throw new Error('Twilio credentials not configured');
    }

    const url = `https://api.twilio.com/2010-04-01/Accounts/${this.twilioAccountSid}/Messages.json`;

    const credentials = btoa(`${this.twilioAccountSid}:${this.twilioAuthToken}`);

    const formData = new URLSearchParams();
    formData.append('From', this.twilioPhoneNumber);
    formData.append('To', phoneNumber);
    formData.append('Body', message);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Basic ${credentials}`,
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: formData
    });

    const data = await response.json();

    if (response.ok) {
      console.log('✅ SMS sent via Twilio:', data.sid);
      return {
        success: true,
        provider: 'twilio',
        messageId: data.sid,
        status: data.status
      };
    } else {
      throw new Error(data.message || 'Twilio API error');
    }
  }

  /**
   * Send via Fast2SMS API
   */
  async _sendViaFast2SMS(phoneNumber, message) {
    if (!this.fast2smsApiKey) {
      throw new Error('Fast2SMS API key not configured');
    }

    const url = 'https://www.fast2sms.com/dev/bulkV2';

    const payload = {
      sender_id: 'FSTSMS',
      message: message,
      route: 'v3',
      numbers: phoneNumber.replace('+91', '')
    };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'authorization': this.fast2smsApiKey,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json();

    if (response.ok && data.return === true) {
      console.log('✅ SMS sent via Fast2SMS:', data.message);
      return {
        success: true,
        provider: 'fast2sms',
        messageId: data.request_id
      };
    } else {
      throw new Error(data.message || 'Fast2SMS API error');
    }
  }

  /**
   * Format phone number
   */
  _formatPhoneNumber(phone) {
    let cleaned = phone.replace(/\D/g, '');

    if (cleaned.startsWith('91')) {
      return '+' + cleaned;
    }

    if (cleaned.length === 10) {
      return '+91' + cleaned;
    }

    return '+' + cleaned;
  }

  /**
   * Check if SMS is configured
   */
  isConfigured() {
    if (this.provider === 'msg91') {
      return !!this.msg91AuthKey;
    } else if (this.provider === 'twilio') {
      return !!(this.twilioAccountSid && this.twilioAuthToken && this.twilioPhoneNumber);
    } else if (this.provider === 'fast2sms') {
      return !!this.fast2smsApiKey;
    }
    return false;
  }

  /**
   * Get SMS cost estimate
   */
  getCostEstimate(messageCount) {
    const costs = {
      msg91: { perSMS: 0.15, currency: 'INR' },
      twilio: { perSMS: 0.75, currency: 'INR' },
      fast2sms: { perSMS: 0.10, currency: 'INR' }
    };

    const providerCost = costs[this.provider] || { perSMS: 0.25, currency: 'INR' };

    return {
      provider: this.provider,
      count: messageCount,
      costPerSMS: providerCost.perSMS,
      totalCost: (messageCount * providerCost.perSMS).toFixed(2),
      currency: providerCost.currency
    };
  }
}

// Singleton instance
const smsService = new SMSService();

export default smsService;
